import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useStore } from "@/context/StoreContext";
import { eur, imgW, sizeRange } from "@/lib/api";

export function ProductCard({ p, index = 0 }) {
  const { t, lang, addToCart, toggleWish, isWished } = useStore();
  const [open, setOpen] = useState(false);
  const wished = isWished(p.id);
  const range = sizeRange(p.sizes);
  const tid = p.slug;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="group relative" data-testid={`product-card-${tid}`}
    >
      <Link to={`/product/${p.slug}`} className="relative block aspect-square overflow-hidden bg-white" data-testid={`product-card-link-${tid}`}>
        <img src={imgW(p.images[0], 800)} alt={p.name} loading="lazy"
          className="absolute inset-0 h-full w-full object-contain transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.04] group-hover:opacity-0" />
        {p.images[1] && (
          <img src={imgW(p.images[1], 800)} alt="" aria-hidden loading="lazy"
            className="absolute inset-0 h-full w-full scale-[1.04] object-contain opacity-0 transition-[opacity,transform] duration-700 ease-out group-hover:scale-100 group-hover:opacity-100" />
        )}
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {p.on_sale && <span className="bg-lavender px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-ink" data-testid={`sale-badge-${tid}`}>-{Math.round((1 - p.final_price / p.price) * 100)}%</span>}
          {p.is_new && <span className="bg-sun px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-ink">New</span>}
          {p.total_stock === 0 && <span className="bg-ink px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-sun">{t("soldOut")}</span>}
        </div>
      </Link>
      <button
        onClick={() => toggleWish(p)} aria-label={t("wishlist")} data-testid={`wishlist-btn-${tid}`}
        className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-sun/90 backdrop-blur transition-transform duration-300 hover:scale-110 active:scale-95"
      >
        <Heart className={`h-4 w-4 transition-colors ${wished ? "fill-lavender text-ink" : "text-ink"}`} strokeWidth={1.8} />
      </button>
      {range && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button data-testid={`quick-add-btn-${tid}`}
              className="absolute bottom-[calc(theme(spacing.24)+12px)] right-3 flex items-center gap-2 bg-ink px-3 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-sun transition-[opacity,transform] duration-300 lg:translate-y-2 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100">
              <Plus className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("quickAdd")}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 rounded-none border-concrete p-4" data-testid={`quick-add-popover-${tid}`}>
            <p className="eyebrow mb-3">{t("selectSize")}</p>
            <div className="grid grid-cols-4 gap-1.5">
              {p.sizes.map((s) => (
                <button key={s.size} disabled={s.stock <= 0} data-testid={`quick-size-${tid}-${s.size}`}
                  onClick={() => { addToCart(p, s.size); setOpen(false); }}
                  className="border border-concrete py-2 font-mono text-xs transition-colors hover:border-ink hover:bg-ink hover:text-sun disabled:cursor-not-allowed disabled:text-ink/25 disabled:line-through disabled:hover:border-concrete disabled:hover:bg-transparent">
                  {s.size}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      <Link to={`/product/${p.slug}`} className="card-info block h-24 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="eyebrow text-ink/50">{p.brand}{p.is_demo && <span className="ml-2 text-ink/30">· {t("demo")}</span>}</p>
            <h3 className="mt-1 line-clamp-1 font-sans text-[15px] font-medium" data-testid={`product-name-${tid}`}>{p.name}</h3>
          </div>
          <div className="shrink-0 text-right font-mono text-sm" data-testid={`product-price-${tid}`}>
            <span className={p.on_sale ? "text-ink" : ""}>{eur(p.final_price, lang)}</span>
            {p.on_sale && <span className="block text-xs text-ink/40 line-through">{eur(p.price, lang)}</span>}
          </div>
        </div>
        <p className="mt-1 font-mono text-[11px] text-ink/50">
          {range ? `EU ${range.from}–${range.to} · ${range.count} ${t("sizes")}` : t("soldOut")}
        </p>
      </Link>
    </motion.article>
  );
}

export function ProductSkeleton() {
  return (
    <div>
      <div className="skeleton-shimmer aspect-square" />
      <div className="skeleton-shimmer mt-4 h-3 w-1/3" />
      <div className="skeleton-shimmer mt-2 h-4 w-2/3" />
    </div>
  );
}
